import { VFXEffectConfig } from '../../VFXEffectRegistry';
import {
  ringBlast, slashArc, shardBurst, glintScatter, groundCloud, risingColumn,
  shadowedSlash, wispRise, starFlash, stagedBlast,
} from './burstBuilders';
import { groundZone } from './zoneBuilders';

// =============================================================================
// PALADIN EFFECTS
// =============================================================================

// Holy trio palette (FX grammar buildout lane 1, 2026-07-17):
const HOLY_GOLD = 0xFFDD44;      // bright gold
const HOLY_GOLD_DEEP = 0xFFD700; // core gold
const HOLY_WHITE = 0xFFF6CC;     // warm white
const HOLY_ACCENT = 0xCC9900;    // deep gold accent

// Per-ability accents (K21 bespoke-impact lane, 2026-07-17) — each high-traffic
// ability keeps the holy gold/cream/white family but owns ONE accent tone:
const HOLY_ZEAL = 0xFFA033;      // crusader zeal — hot orange-gold
const HOLY_STEEL = 0xDCE8F5;     // avenger shield — cold steel-white
const HOLY_BRONZE = 0xB87A2E;    // hammer — struck bronze

/**
 * Holy heal - green/gold flash for healing spells
 */
const HOLY_HEAL: VFXEffectConfig = {
  id: 'holy_heal',
  name: 'Holy Heal',
  category: 'burst',
  description: 'Golden flash for holy healing spells',
  primitives: [
    // Outer green glow (expanding)
    {
      type: 'circle',
      radius: 45,
      color: 0xFFD700,
      alpha: 0.5,
      depth: 94,
      tween: {
        alpha: 0,
        scale: 1.6,
        duration: 600,
        ease: 'Power2'
      }
    },
    // Inner golden core
    {
      type: 'circle',
      radius: 20,
      color: 0xFFDD44,
      alpha: 0.7,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 1.8,
        duration: 400,
        ease: 'Power2'
      }
    },
    // Rising golden stars
    {
      type: 'star',
      points: 4,
      innerRadius: 4,
      outerRadius: 12,
      color: 0xFFFF00,
      alpha: 0.8,
      depth: 96,
      tween: {
        rotation: Math.PI * 0.25,
        y: -25,
        alpha: 0,
        scale: 1.5,
        duration: 500,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 30 }
    },
    // White sparkle burst
    {
      type: 'circle',
      radius: 3,
      color: 0xFFFFFF,
      alpha: 0.9,
      depth: 97,
      tween: {
        alpha: 0,
        scale: 0.5,
        duration: 350,
        ease: 'Power2'
      },
      scatter: { count: 4, radius: 20 }
    }
  ],
  screenFlash: { color: 0xFFD700, duration: 150 }
};

/**
 * Divine shield bubble - golden protective barrier
 */
const DIVINE_SHIELD_BUBBLE: VFXEffectConfig = {
  id: 'divine_shield',
  name: 'Divine Shield',
  category: 'aura',
  description: 'Golden protective barrier around the caster',
  followTarget: 'player',
  primitives: [
    // Outer golden shell (pulsing with stroke)
    {
      type: 'ellipse',
      width: 80,
      height: 90,
      color: 0xFFD700,
      alpha: 0.15,
      depth: 50,
      stroke: { color: 0xFFFF00, width: 3 },
      tween: {
        rotation: Math.PI * 0.05,
        scale: 1.05,
        duration: 250,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1
      }
    },
    // Inner soft glow
    {
      type: 'ellipse',
      width: 60,
      height: 70,
      color: 0xFFFF88,
      alpha: 0.08,
      depth: 49,
      tween: {
        scale: 1.08,
        alpha: 0.03,
        duration: 300,
        yoyo: true,
        repeat: -1
      }
    },
    // Orbiting golden particles
    {
      type: 'circle',
      radius: 4,
      color: 0xFFFF00,
      alpha: 0.6,
      depth: 51,
      tween: {
        alpha: 0,
        y: -20,
        scale: 1.5,
        duration: 700,
        ease: 'Power2'
      },
      scatter: { count: 8, radius: 48 }
    },
    // Holy cross sparkles
    {
      type: 'star',
      points: 4,
      innerRadius: 2,
      outerRadius: 5,
      color: 0xFFFFFF,
      alpha: 0.7,
      depth: 52,
      tween: {
        rotation: Math.PI * 0.25,
        alpha: 0,
        duration: 500,
        repeat: -1,
        delay: 200
      },
      scatter: { count: 3, radius: 42 }
    }
  ]
};

/**
 * Consecration field - holy ground zone effect
 */
const CONSECRATION_FIELD: VFXEffectConfig = {
  id: 'consecration',
  name: 'Consecration',
  category: 'zone',
  description: 'Holy ground zone for consecration ability',
  // Zone lifetime contract: world-anchored + looping, so the def-level
  // duration ceiling is mandatory (PF-ZONE-NO-DURATION). 5000 = the summon's
  // gameplay duration (ConsecrationAbility.ts).
  duration: 5000,
  primitives: [
    // Golden ground zone (pulsing with stroke)
    {
      type: 'ellipse',
      width: 200,
      height: 100,
      color: 0xFFD700,
      alpha: 0.1,
      depth: 10,
      stroke: { color: 0xFFFF00, width: 2 },
      tween: {
        alpha: 0.2,
        scale: 1.03,
        duration: 1000,
        ease: 'Quad.easeInOut',
        yoyo: true,
        repeat: -1
      }
    },
    // Rotating holy stars (slow continuous rotation)
    {
      type: 'star',
      points: 4,
      innerRadius: 5,
      outerRadius: 15,
      color: 0xFFFFFF,
      alpha: 0.5,
      depth: 11,
      tween: {
        rotation: Math.PI * 0.5,
        alpha: 0,
        duration: 1500,
        repeat: -1
      },
      scatter: { count: 12, radius: 80 }
    },
    // Rising holy motes (small golden circles drifting up)
    {
      type: 'circle',
      radius: 3,
      color: 0xFFDD44,
      alpha: 0.6,
      depth: 12,
      tween: {
        y: -30,
        alpha: 0,
        scale: 0.5,
        duration: 800,
        repeat: -1
      },
      scatter: { count: 6, radius: 70 }
    }
  ]
};

/**
 * Holy fire zone - burning holy ground effect
 */
const HOLY_FIRE_ZONE: VFXEffectConfig = {
  id: 'holy_fire',
  name: 'Holy Fire',
  category: 'zone',
  description: 'Burning holy ground for holy fire ability',
  // Was the ice_wall leak class: registered + repeat:-1 + no duration (plays
  // as consecration's hit zone). 5000 matches the consecration burn window.
  duration: 5000,
  primitives: [
    // Fire ground zone (pulsing)
    {
      type: 'ellipse',
      width: 150,
      height: 60,
      color: 0xFF4500,
      alpha: 0.15,
      depth: 10,
      stroke: { color: 0xFF6600, width: 2 },
      tween: {
        scale: 1.1,
        alpha: 0.25,
        duration: 400,
        ease: 'Power2',
        yoyo: true,
        repeat: -1
      }
    },
    // Rising flame particles (yellow)
    {
      type: 'circle',
      radius: 6,
      color: 0xFFFF00,
      alpha: 0.6,
      depth: 11,
      tween: {
        y: -40,
        alpha: 0,
        scale: 0.3,
        duration: 500,
        repeat: -1
      },
      scatter: { count: 8, radius: 55 }
    },
    // Orange fire motes
    {
      type: 'circle',
      radius: 4,
      color: 0xFF6600,
      alpha: 0.5,
      depth: 12,
      tween: {
        y: -25,
        alpha: 0,
        scale: 1.5,
        duration: 700,
        repeat: -1,
        delay: 100
      },
      scatter: { count: 5, radius: 50 }
    },
    // White hot center flickers
    {
      type: 'circle',
      radius: 2,
      color: 0xFFFFFF,
      alpha: 0.8,
      depth: 13,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 300,
        repeat: -1,
        delay: 150
      },
      scatter: { count: 4, radius: 30 }
    }
  ]
};

/**
 * Consecration placement id — SummonBehavior places the field with
 * vfxId 'consecration_field' (ConsecrationAbility.ts); before this batch the
 * id was unregistered and fell to the legacy particle fallback. Registering
 * the same zone def under the placement id routes the summon through the
 * tracked-persistent path (instanceId → playPersistent → summon_expired stop).
 */
const CONSECRATION_FIELD_PLACEMENT: VFXEffectConfig = {
  ...CONSECRATION_FIELD,
  id: 'consecration_field',
  description: 'Consecration field as placed by the summon path',
};

/**
 * Sanctified Ground — persistent holy healing field (first registered def;
 * the id previously aliased to consecration in the cast map). Softer,
 * white-gold serenity vs consecration's burning gold — same holy family,
 * distinct silhouette. Pulse synced to the 1000ms HoT tick.
 * Duration = the SUMMON's 10s (SanctifiedGroundAbility.ts); the 15s TOML
 * duration_ms mismatch is a content-lane fix, flagged in the zone spec §2.
 */
const SANCTIFIED_GROUND: VFXEffectConfig = {
  id: 'sanctified_ground',
  name: 'Sanctified Ground',
  category: 'zone',
  description: 'Persistent holy ground that mends allies standing within',
  duration: 10000,
  primitives: [
    ...groundZone({
      ring: { width: 190, fill: 0xFFF2C0, rim: 0xFFD700, alpha: 0.14, rimWidth: 2, pulse: { alpha: 0.24, scale: 1.04, duration: 1000 } },
      edge: { count: 6, rimRadius: 88, shape: 'star', size: 6, color: 0xFFEE99, alpha: 0.65, speed: 0.1, glow: true },
      fall: { direction: 'up', count: 7, spread: 75, color: 0xE8FFE0, alpha: 0.7, travel: 36, periodMs: 1100, glow: true },
      pulse: { radius: 34, color: 0xFFE066, alpha: 0.3, periodMs: 1000, expandTo: 2.1, stroke: { color: 0xFFF8DC, width: 1 } },
    }),
  ],
};

/**
 * Retribution spikes - golden thorns for retribution aura
 */
const RETRIBUTION_SPIKES: VFXEffectConfig = {
  id: 'retribution_spikes',
  name: 'Retribution Aura',
  category: 'aura',
  description: 'Golden thorns for retribution aura buff',
  followTarget: 'player',
  primitives: [
    // Slowly rotating star shell
    {
      type: 'star',
      points: 8,
      innerRadius: 35,
      outerRadius: 45,
      color: 0xFFD700,
      alpha: 0.3,
      depth: 50,
      stroke: { color: 0xFFFF00, width: 2 },
      tween: {
        rotation: Math.PI * 0.25,
        scale: 1.08,
        duration: 1000,
        ease: 'Linear',
        yoyo: true,
        repeat: -1
      }
    },
    // Thorn spark particles at the tips
    {
      type: 'circle',
      radius: 3,
      color: 0xFFFF88,
      alpha: 0.6,
      depth: 51,
      tween: {
        alpha: 0,
        scale: 1.5,
        duration: 600,
        repeat: -1,
        delay: 100
      },
      scatter: { count: 8, radius: 45 }
    }
  ]
};

/**
 * Judgment beam - pillar of light from sky
 */
const JUDGMENT_BEAM: VFXEffectConfig = {
  id: 'judgment',
  name: 'Judgment Beam',
  category: 'burst',
  description: 'Holy pillar of light for judgment ability',
  primitives: [
    // Outer golden glow beam (wide, soft)
    {
      type: 'rect',
      width: 60,
      height: 600,
      offsetY: -300,
      color: 0xFFD700,
      alpha: 0.25,
      depth: 93,
      tween: {
        scaleX: 0,
        alpha: 0,
        duration: 500,
        ease: 'Quad.easeIn'
      }
    },
    // Core white beam (narrow, bright)
    {
      type: 'rect',
      width: 40,
      height: 600,
      offsetY: -300,
      color: 0xFFFFFF,
      alpha: 0.8,
      depth: 95,
      tween: {
        scaleX: 0,
        alpha: 0,
        duration: 400,
        ease: 'Quad.easeIn'
      }
    },
    // Ground impact flash (expanding ellipse)
    {
      type: 'ellipse',
      width: 120,
      height: 40,
      color: 0xFFD700,
      alpha: 0.7,
      depth: 94,
      tween: {
        scaleX: 2,
        alpha: 0,
        duration: 500,
        ease: 'Power2'
      }
    },
    // Holy sparks from impact
    {
      type: 'star',
      points: 4,
      innerRadius: 3,
      outerRadius: 8,
      color: 0xFFFFFF,
      alpha: 0.9,
      depth: 96,
      tween: {
        rotation: Math.PI * 0.5,
        y: -20,
        alpha: 0,
        duration: 400,
        ease: 'Power2'
      },
      scatter: { count: 6, radius: 30 }
    },
    // Bright center flash
    {
      type: 'circle',
      radius: 15,
      color: 0xFFFFFF,
      alpha: 1,
      depth: 97,
      tween: {
        alpha: 0,
        scale: 3,
        duration: 300,
        ease: 'Power1'
      }
    }
  ],
  screenShake: { intensity: 12, duration: 400 },
  screenFlash: { color: 0xFFFFFF, duration: 200 }
};

/**
 * Holy Strike Cast Glow — golden glow during holy strike wind-up
 */
const HOLY_STRIKE_CAST_GLOW: VFXEffectConfig = {
  id: 'holy_strike_cast_glow',
  name: 'Holy Strike Cast Glow',
  category: 'burst',
  description: 'Golden glow during holy strike wind-up',
  primitives: [
    {
      type: 'circle',
      radius: 20,
      color: 0xFFD700,
      alpha: 0.6,
      depth: 94,
      tween: { scale: 1.8, alpha: 0, duration: 350, ease: 'Power2' }
    },
    {
      type: 'star',
      points: 4,
      innerRadius: 5,
      outerRadius: 12,
      color: 0xFFFFCC,
      alpha: 0.8,
      depth: 95,
      tween: { rotation: Math.PI * 0.5, alpha: 0, duration: 400 }
    }
  ]
};

/**
 * Retribution Aura — persistent golden ring around paladin
 */
const RETRIBUTION_AURA_AURA: VFXEffectConfig = {
  id: 'retribution_aura_aura',
  name: 'Retribution Aura',
  category: 'aura',
  description: 'Persistent golden retribution ring',
  followTarget: 'player',
  primitives: [
    {
      type: 'ellipse',
      width: 80,
      height: 80,
      color: 0xFFD700,
      alpha: 0.15,
      depth: 50,
      stroke: { color: 0xFFD700, width: 2 },
      tween: { scale: 1.05, alpha: 0.25, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' }
    }
  ]
};

/**
 * Wake of Ashes burst - holy fire explosion for wake of ashes ability
 */
const WAKE_OF_ASHES_BURST: VFXEffectConfig = {
  id: 'wake_of_ashes_burst',
  name: 'Wake of Ashes',
  category: 'burst',
  description: 'Holy fire explosion for wake of ashes ability',
  screenShake: { intensity: 10, duration: 300 },
  screenFlash: { color: 0xFFD700, duration: 150 },
  primitives: [
    // Massive golden shockwave ring
    { type: 'circle', radius: 50, color: 0xFFD700, alpha: 0.6, depth: 93,
      stroke: { color: 0xFFFF00, width: 3 },
      tween: { alpha: 0, scale: 2.5, duration: 450, ease: 'Power2' } },
    // Inner holy fire core
    { type: 'circle', radius: 25, color: 0xFF6600, alpha: 0.8, depth: 95,
      tween: { alpha: 0, scale: 2, duration: 300, ease: 'Power2' } },
    // White-hot center flash
    { type: 'circle', radius: 12, color: 0xFFFFFF, alpha: 1, depth: 97,
      tween: { alpha: 0, scale: 1.5, duration: 200, ease: 'Power1' } },
    // Holy ember scatter
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 8, color: 0xFFDD44, alpha: 0.9, depth: 96,
      tween: { rotation: Math.PI * 0.5, alpha: 0, duration: 500, ease: 'Power2' },
      scatter: { count: 10, radius: 60 } },
    // Ash particles rising
    { type: 'circle', radius: 4, color: 0x888888, alpha: 0.6, depth: 94,
      tween: { y: -25, alpha: 0, scale: 0.3, duration: 600, ease: 'Power2' },
      scatter: { count: 6, radius: 45 } }
  ]
};

// =============================================================================
// EXPORTS
// =============================================================================


/**
 * Holy Radiance Burst — paladin AoE heal ultimate. SPEC: a radiant golden
 * nova expands from the caster (ring + soft white-gold bloom), a cross flare
 * spins up, and holy motes rise. Warm, generous, unmistakably HOLY — the
 * gold family, not the green heal.
 */
const HOLY_RADIANCE_BURST: VFXEffectConfig = {
  id: 'holy_radiance_burst',
  name: 'Holy Radiance',
  category: 'burst',
  description: 'AoE heal nova — expanding gold ring + cross flare + rising motes',
  screenFlash: { color: 0xFFF3B0, duration: 180 },
  primitives: [
    { type: 'particles', particleEffectId: 'holy_burst', particleCount: 10 },
    // Expanding radiant ring.
    { type: 'circle', radius: 30, color: 0xFFD700, alpha: 0.6, depth: 94,
      tween: { alpha: 0, scale: 2.8, duration: 550, ease: 'Power2' } },
    // Soft outer bloom.
    { type: 'circle', radius: 48, color: 0xFFF3B0, alpha: 0.35, depth: 93,
      tween: { alpha: 0, scale: 2.2, duration: 650, ease: 'Power2' } },
    // Cross flare — 4-point star spinning up.
    { type: 'star', points: 4, innerRadius: 6, outerRadius: 22, color: 0xFFFFFF, alpha: 0.85, depth: 96,
      tween: { rotation: Math.PI * 0.5, alpha: 0, scale: 1.6, duration: 500, ease: 'Power2' } },
    // Rising blessing sparks.
    { type: 'circle', radius: 4, color: 0xFFEE99, alpha: 0.9, depth: 95,
      tween: { y: -26, alpha: 0, scale: 0.4, duration: 600, ease: 'Power1' },
      scatter: { count: 6, radius: 30 } },
  ],
};

// =============================================================================
// HOLY TRIO — bespoke defs for the 9 TOML-authored abilities that previously
// aliased to one generic look (FX grammar buildout lane 1, 2026-07-17).
// Registering these names statically wins over namedEffectGenerator.
// Silhouette contract (vs hit_holy's bloom + cross-flare + gold-shard read):
//   holy_burst     → ring + rising COLUMN + glints (offense pop, no star flare)
//   holy_explosion → DOUBLE ring + rect shard slivers + ground glow (the big one)
//   holy_slash     → blade ARCS, no radial ring at all (melee-weight)
// =============================================================================

/**
 * Holy Burst — a radiant OFFENSIVE pop. Gold ring blast with a deep-gold rim,
 * warm-white glints, and a small rising light column for holy verticality.
 * Reads as damage, not heal: hard-edged ring + hot core flash instead of the
 * heal's soft expanding bloom.
 */
const HOLY_BURST: VFXEffectConfig = {
  id: 'holy_burst',
  name: 'Holy Burst',
  category: 'burst',
  description: 'Radiant offensive pop — gold ring blast + white glints + rising light column',
  screenFlash: { color: HOLY_GOLD, duration: 120 },
  primitives: [
    // Gold ring blast with deep-accent rim — the impact verb.
    ringBlast({
      radius: 26, color: HOLY_GOLD_DEEP, alpha: 0.7, depth: 94, glow: true,
      stroke: { color: HOLY_ACCENT, width: 2 },
      expandTo: 2.4, durationMs: 320,
    }),
    // Small rising light column — holy verticality beat.
    risingColumn({
      width: 10, height: 70, color: HOLY_WHITE, alpha: 0.75, depth: 95, glow: true,
      riseBy: 30, durationMs: 360,
    }),
    // Warm-white glints finishing the pop.
    glintScatter({ count: 6, spread: 30, color: HOLY_WHITE, durationMs: 240 }),
    // Bespoke flavor: hot inner flash core — the "this HURT" offense read.
    {
      type: 'circle', radius: 10, color: HOLY_GOLD, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, scale: 1.6, duration: 180, ease: 'Power1' },
    },
  ],
};

/**
 * Holy Explosion — the BIG one. Double ring blast (gold core + warm-white
 * edge), a shard burst of light slivers, and a lingering deep-gold ground
 * glow. Bigger radii and longer durations than holy_burst, with shake.
 */
const HOLY_EXPLOSION: VFXEffectConfig = {
  id: 'holy_explosion',
  name: 'Holy Explosion',
  category: 'burst',
  description: 'Big holy detonation — double ring (gold core + white edge) + light slivers + ground glow',
  screenShake: { intensity: 10, duration: 350 },
  screenFlash: { color: HOLY_WHITE, duration: 200 },
  primitives: [
    // Inner gold core ring — fast, hard.
    ringBlast({
      radius: 40, color: HOLY_GOLD_DEEP, alpha: 0.8, depth: 94, glow: true,
      stroke: { color: HOLY_ACCENT, width: 3 },
      expandTo: 3.0, durationMs: 550,
    }),
    // Outer warm-white edge ring — slower, softer, sells the scale.
    ringBlast({
      radius: 52, color: HOLY_WHITE, alpha: 0.5, depth: 93,
      expandTo: 3.4, durationMs: 650, ease: 'Sine.easeOut',
    }),
    // Light slivers thrown outward — the shatter-of-light beat.
    shardBurst({
      count: 10, spread: 55, color: HOLY_GOLD, alpha: 0.9, depth: 95, glow: true,
      shard: { width: 14, height: 3 },
      travel: 110, durationMs: 480,
    }),
    // Deep-gold ground glow lingering under the blast.
    groundCloud({
      width: 130, height: 44, color: HOLY_ACCENT, alpha: 0.4, depth: 92,
      stroke: { color: HOLY_GOLD_DEEP, width: 2 },
      swellTo: 1.8, durationMs: 750,
    }),
    // Late warm-white glints as the light settles.
    glintScatter({ count: 8, spread: 65, color: HOLY_WHITE, durationMs: 320, delayMs: 120 }),
  ],
};

/**
 * Holy Slash — a fast gold blade arc with a warm-white glint follow.
 * Melee-weight: arcs only, deliberately NO radial ring so it never reads
 * as a burst/heal — it reads as a weapon swing wreathed in light.
 */
const HOLY_SLASH: VFXEffectConfig = {
  id: 'holy_slash',
  name: 'Holy Slash',
  category: 'burst',
  description: 'Fast gold blade arc + white glint follow — melee-weight, no radial burst',
  primitives: [
    // Main gold blade arc — the swing.
    slashArc({
      radius: 42, startDeg: -60, endDeg: 60, width: 9,
      color: HOLY_GOLD_DEEP, alpha: 0.95, depth: 96, glow: true,
      durationMs: 220,
    }),
    // Warm-white glint follow — a thinner trailing arc a beat behind.
    slashArc({
      radius: 34, startDeg: -50, endDeg: 50, width: 4,
      color: HOLY_WHITE, alpha: 0.85, depth: 97,
      durationMs: 200, fadeDelayMs: 60,
    }),
    // A few bright gold sparks off the blade edge.
    glintScatter({ count: 4, spread: 26, color: HOLY_GOLD, radius: 2, durationMs: 200, delayMs: 40 }),
  ],
};

// =============================================================================
// K21 BESPOKE IMPACTS — the seven highest-traffic paladin abilities stop
// sharing the trio and each get an owned silhouette (heat-map top item,
// 2026-07-17). Wiring: conductor points TOML impact_effect at these ids and
// retires the shadowing AbilityVFXMap hit_heal rows — defs are inert until then.
// Silhouette contract (each distinct from the trio AND each other):
//   crusader_strike → zeal slash + hot contact core (arcs, no star)
//   templars_verdict → crossed X blades + spinning verdict seal star
//   avenger_shield  → steel star pop + ricochet slivers
//   smite           → descending white lance + contact ring
//   holy_wrath      → dual-pulse nova + rising burn-off wisps + wrath star
//   final_reckoning → reckoning beam slam + staged detonation + ground scar
//   hammer_throw    → bronze-rimmed clang + reverberation ring + knock-loose sparks
// =============================================================================

/**
 * Crusader Strike — zealous overhead blade. Deep-gold blade arc with a hot
 * zeal-orange trailing edge, closing on a white-hot contact core. Deliberately
 * NO star flare and no radial ring — it reads as muscle, not radiance.
 */
const CRUSADER_STRIKE_IMPACT: VFXEffectConfig = {
  id: 'crusader_strike_impact',
  name: 'Crusader Strike',
  category: 'burst',
  description: 'Zealous overhead blade — gold arc + zeal-orange trail + white-hot contact core',
  primitives: [
    // Overhead-committed swing: steep entry, shallow exit.
    ...shadowedSlash({
      radius: 44, startDeg: -75, endDeg: 30,
      blade: { color: HOLY_GOLD_DEEP, width: 10, alpha: 0.95, glow: true },
      trail: { color: HOLY_ZEAL, width: 5, alpha: 0.6, delayMs: 50 },
      durationMs: 230, depth: 96,
    }),
    // White-hot contact core at the bite point.
    {
      type: 'circle', radius: 9, color: HOLY_WHITE, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, scale: 1.7, duration: 190, ease: 'Power1', delay: 60 },
    },
    // Zeal sparks off the edge.
    glintScatter({ count: 4, spread: 24, color: HOLY_ZEAL, durationMs: 200, delayMs: 80 }),
  ],
};

/**
 * Templar's Verdict — the sentence, passed. Two crossed blades of light form
 * an X, then a heavy 4-point verdict seal spins up at the crossing point.
 * The star IS the signature — the slash pair only frames it.
 */
const TEMPLARS_VERDICT_IMPACT: VFXEffectConfig = {
  id: 'templars_verdict_impact',
  name: "Templar's Verdict",
  category: 'burst',
  description: 'Crossed X blades framing a spinning 4-point verdict seal',
  screenFlash: { color: HOLY_WHITE, duration: 140 },
  primitives: [
    // The X — a deep-gold downstroke and a lighter answering cross-cut.
    slashArc({
      radius: 46, startDeg: -75, endDeg: 15, width: 9,
      color: HOLY_GOLD_DEEP, alpha: 0.95, depth: 96, glow: true, durationMs: 240,
    }),
    slashArc({
      radius: 46, startDeg: 165, endDeg: 255, width: 9,
      color: HOLY_GOLD, alpha: 0.85, depth: 95, durationMs: 240, fadeDelayMs: 70,
    }),
    // The verdict seal — heavy white cross-star stamped at the crossing.
    starFlash({
      points: 4, innerRadius: 7, outerRadius: 24, color: 0xFFFFFF, alpha: 0.95,
      depth: 97, glow: true, spinRad: Math.PI * 0.25, expandTo: 1.7,
      durationMs: 320, delayMs: 110,
    }),
    // Cream glints settling as the sentence lands.
    glintScatter({ count: 6, spread: 34, color: HOLY_WHITE, durationMs: 240, delayMs: 140 }),
  ],
};

/**
 * Avenger's Shield — a thrown shield CLANG, not a spell. Cold steel-white
 * 5-point pop (the shield rim), a tight gold ring at contact, and ricochet
 * slivers thrown off in steel — the only paladin impact with a cold accent.
 */
const AVENGER_SHIELD_IMPACT: VFXEffectConfig = {
  id: 'avenger_shield_impact',
  name: "Avenger's Shield",
  category: 'burst',
  description: 'Shield clang — steel 5-point rim pop + gold contact ring + ricochet slivers',
  primitives: [
    // The shield rim read — 5-point steel star spinning off the hit.
    starFlash({
      points: 5, innerRadius: 11, outerRadius: 19, color: HOLY_STEEL, alpha: 0.9,
      depth: 96, glow: true, spinRad: Math.PI * 0.6, expandTo: 1.6, durationMs: 280,
    }),
    // Tight gold contact ring with a steel rim.
    ringBlast({
      radius: 20, color: HOLY_GOLD_DEEP, alpha: 0.75, depth: 94, glow: true,
      stroke: { color: HOLY_STEEL, width: 2 },
      expandTo: 2.0, durationMs: 260,
    }),
    // Ricochet slivers — steel, flat, fast.
    shardBurst({
      count: 6, spread: 40, color: HOLY_STEEL, alpha: 0.9, depth: 95, glow: true,
      shard: { width: 10, height: 3 }, travel: 80, durationMs: 300,
    }),
    // Gold sparks off the clang.
    glintScatter({ count: 4, spread: 22, color: HOLY_GOLD, durationMs: 200, delayMs: 50 }),
  ],
};

/**
 * Smite — a lance of light from above. A narrow white bolt stabs DOWN into
 * the target (the file's only descending-lance read), then a small gold ring
 * confirms contact. Quick, precise, priestly — the caster's poke.
 */
const SMITE_IMPACT: VFXEffectConfig = {
  id: 'smite_impact',
  name: 'Smite',
  category: 'burst',
  description: 'Descending white light-lance + small gold contact ring',
  primitives: [
    // The lance — starts above the target, stabs down, thins as it lands.
    {
      type: 'rect', width: 8, height: 90, offsetY: -80,
      color: 0xFFFFFF, alpha: 0.9, depth: 96, glow: true,
      tween: { y: 34, scaleX: 0.3, alpha: 0, duration: 260, ease: 'Quad.easeIn' },
    },
    // Contact ring — small, warm, immediate.
    ringBlast({
      radius: 15, color: HOLY_GOLD, alpha: 0.8, depth: 94, glow: true,
      expandTo: 2.0, durationMs: 260, delayMs: 90,
    }),
    // Warm-white pinpricks where the lance grounded.
    glintScatter({ count: 5, spread: 22, color: HOLY_WHITE, durationMs: 220, delayMs: 120 }),
  ],
};

/**
 * Holy Wrath — righteous anger as a nova. White inner pulse then deep-gold
 * outer wave (staged dual-pulse), burn-off wisps rising from everything it
 * touched, and a wrath star at the center. Angrier than holy_explosion's
 * shatter — this one BURNS upward.
 */
const HOLY_WRATH_IMPACT: VFXEffectConfig = {
  id: 'holy_wrath_impact',
  name: 'Holy Wrath',
  category: 'burst',
  description: 'Dual-pulse white/gold nova + rising burn-off wisps + wrath star',
  screenShake: { intensity: 7, duration: 250 },
  screenFlash: { color: HOLY_GOLD, duration: 150 },
  primitives: [
    // Staged dual-pulse: hard white core, then the deep-gold wave.
    ...stagedBlast(
      { radius: 30, color: 0xFFFFFF, alpha: 0.85, depth: 95, glow: true, expandTo: 2.2, durationMs: 300 },
      { radius: 44, color: HOLY_GOLD_DEEP, alpha: 0.6, depth: 94, stroke: { color: HOLY_ACCENT, width: 2 }, expandTo: 2.6, durationMs: 380 },
      90,
    ),
    // Burn-off — gold wisps rising from the scorched ring.
    wispRise({ count: 7, spread: 42, color: HOLY_GOLD, alpha: 0.85, travel: 36, durationMs: 480, glow: true, depth: 96 }),
    // The wrath star snapping open between the pulses.
    starFlash({
      points: 4, innerRadius: 5, outerRadius: 16, color: HOLY_WHITE, alpha: 0.9,
      depth: 97, spinRad: Math.PI * 0.5, expandTo: 1.8, durationMs: 300, delayMs: 90,
    }),
  ],
};

/**
 * Final Reckoning — the cooldown moment. A wide reckoning beam SLAMS down,
 * detonates in a staged white/gold blast, throws gold slivers, and leaves a
 * deep-gold scar on the ground. Shares the beam verb with smite but at 4x
 * the width and with a full detonation under it. Also wired for
 * final_reckoning_2.
 */
const FINAL_RECKONING_IMPACT: VFXEffectConfig = {
  id: 'final_reckoning_impact',
  name: 'Final Reckoning',
  category: 'burst',
  description: 'Reckoning beam slam + staged white/gold detonation + gold slivers + ground scar',
  screenShake: { intensity: 12, duration: 400 },
  screenFlash: { color: 0xFFFFFF, duration: 200 },
  primitives: [
    // The reckoning beam — wide judgment column slamming down.
    {
      type: 'rect', width: 34, height: 180, offsetY: -120,
      color: HOLY_WHITE, alpha: 0.85, depth: 95, glow: true,
      tween: { y: 40, scaleX: 0.2, alpha: 0, duration: 300, ease: 'Quad.easeIn' },
    },
    // Staged detonation timed to the beam landing.
    ...stagedBlast(
      { radius: 34, color: 0xFFFFFF, alpha: 0.9, depth: 96, glow: true, expandTo: 2.4, durationMs: 320, delayMs: 120 },
      { radius: 50, color: HOLY_GOLD_DEEP, alpha: 0.7, depth: 94, stroke: { color: HOLY_ACCENT, width: 3 }, expandTo: 3.0, durationMs: 400, delayMs: 120 },
      60,
    ),
    // Gold slivers thrown from the blast point.
    shardBurst({
      count: 9, spread: 56, color: HOLY_GOLD, alpha: 0.9, depth: 95, glow: true,
      shard: { width: 13, height: 3 }, travel: 100, durationMs: 380, delayMs: 160,
    }),
    // The scar — deep-gold ground glow left where the verdict landed.
    groundCloud({
      width: 140, height: 46, color: HOLY_ACCENT, alpha: 0.4, depth: 92,
      stroke: { color: HOLY_GOLD_DEEP, width: 2 },
      swellTo: 1.9, durationMs: 430,
    }),
  ],
};

/**
 * Hammer Throw — blunt weight, not light. A bronze-rimmed gold CLANG ring, a
 * thinner reverberation ring a beat later, and short sparks knocked loose.
 * The only paladin impact keyed to bronze — it reads as metal on bone.
 */
const HAMMER_THROW_IMPACT: VFXEffectConfig = {
  id: 'hammer_throw_impact',
  name: 'Hammer Throw',
  category: 'burst',
  description: 'Bronze-rimmed clang ring + reverberation ring + knocked-loose sparks',
  screenShake: { intensity: 6, duration: 200 },
  primitives: [
    // The clang — thick bronze rim on a gold ring.
    ringBlast({
      radius: 24, color: HOLY_GOLD_DEEP, alpha: 0.85, depth: 94, glow: true,
      stroke: { color: HOLY_BRONZE, width: 4 },
      expandTo: 2.0, durationMs: 280,
    }),
    // Reverberation — thinner bronze ring a beat behind.
    ringBlast({
      radius: 30, color: HOLY_BRONZE, alpha: 0.5, depth: 93,
      expandTo: 2.4, durationMs: 300, delayMs: 110, ease: 'Sine.easeOut',
    }),
    // Short heavy sparks knocked loose by the blow.
    shardBurst({
      count: 7, spread: 38, color: HOLY_GOLD, alpha: 0.9, depth: 95, glow: true,
      shard: { width: 8, height: 2 }, travel: 70, durationMs: 300,
    }),
    // Warm-white flecks off the metal.
    glintScatter({ count: 4, spread: 20, color: HOLY_WHITE, durationMs: 200, delayMs: 60 }),
  ],
};

export const PALADIN_VFX: VFXEffectConfig[] = [
  HOLY_BURST,
  HOLY_EXPLOSION,
  HOLY_SLASH,
  CRUSADER_STRIKE_IMPACT,
  TEMPLARS_VERDICT_IMPACT,
  AVENGER_SHIELD_IMPACT,
  SMITE_IMPACT,
  HOLY_WRATH_IMPACT,
  FINAL_RECKONING_IMPACT,
  HAMMER_THROW_IMPACT,
  HOLY_RADIANCE_BURST,
  HOLY_HEAL,
  DIVINE_SHIELD_BUBBLE,
  CONSECRATION_FIELD,
  CONSECRATION_FIELD_PLACEMENT,
  SANCTIFIED_GROUND,
  HOLY_FIRE_ZONE,
  RETRIBUTION_SPIKES,
  JUDGMENT_BEAM,
  HOLY_STRIKE_CAST_GLOW,
  RETRIBUTION_AURA_AURA,
  WAKE_OF_ASHES_BURST,
];
